/**
 * Partial polyfill of `SVGTransformList`
 * @module SVGTransformList
 *
 * @license MIT
 *
 * @copyright 2010 Alexis Deveria, 2010 Jeff Schiller
 */

import { NS } from '../common/namespaces.js';
import { noteTransformListAccess } from './mutation-tracking';

const svgroot = document.createElementNS(NS.SVG, 'svg') as unknown as SVGSVGElement;

/**
 * Helper function to convert `SVGTransform` to a string.
 * @param {SVGTransform} xform
 * @returns {string}
 */
export function transformToString(xform: SVGTransform): string {
    const m = xform.matrix;
    let text = '';

    switch (xform.type) {
        case 1: // MATRIX
            text = 'matrix(' + [m.a, m.b, m.c, m.d, m.e, m.f].join(',') + ')';
            break;
        case 2: // TRANSLATE
            text = 'translate(' + m.e + ',' + m.f + ')';
            break;
        case 3: // SCALE
            text = (m.a === m.d) ? `scale(${m.a})` : `scale(${m.a},${m.d})`;
            break;
        case 4: { // ROTATE
            let cx = 0;
            let cy = 0;
            // this prevents divide by zero
            if (xform.angle !== 0) {
                const K = 1 - m.a;
                cy = (K * m.f + m.b * m.e) / (K * K + m.b * m.b);
                cx = (m.e - m.b * cy) / K;
            }
            text = 'rotate(' + xform.angle + ' ' + cx + ',' + cy + ')';
            break;
        }
    }

    return text;
}

/**
 * Map of SVGTransformList objects.
 */
let listMap_ = {};

/**
* @function module:SVGTransformList.resetListMap
* @returns {void}
*/
export const resetListMap = function () {
    listMap_ = {};
};

/**
 * Removes transforms of the given element from the map.
 * @function module:SVGTransformList.removeElementFromListMap
 * @param {Element} elem - a DOM Element
 * @returns {void}
 */
export let removeElementFromListMap = function (elem: Element) {
    if (elem.id && listMap_[elem.id]) {
        delete listMap_[elem.id];
    }
};

/**
* Returns an object that behaves like a `SVGTransformList` for the given DOM element.
* @function module:SVGTransformList.getTransformList
* @param {Element} elem - DOM element to get a transformlist from
* @todo The polyfill should have `SVGAnimatedTransformList` and this should use it
* @returns {SVGTransformList}
*/
export const getTransformList = function (elem: SVGGraphicsElement) : SVGTransformList | null {
    if (!elem) {
        return null;
    }

    // Edits to the returned list don't produce mutation records, so let the document sync compare instead.
    if (elem.transform || elem instanceof SVGGradientElement || elem instanceof SVGPatternElement) {
        noteTransformListAccess(elem);
    }

    if (elem.transform) {
        return elem.transform.baseVal;
    }

    if (elem instanceof SVGGradientElement) {
        return elem.gradientTransform.baseVal;
    }

    if (elem instanceof SVGPatternElement) {
        return elem.patternTransform.baseVal;
    }

    return null;
};

/**
* @callback module:SVGTransformList.removeElementFromListMap
* @param {Element} elem
* @returns {void}
*/
/**
* Replace `removeElementFromListMap` for unit-testing.
* @function module:SVGTransformList.changeRemoveElementFromListMap
* @param {module:SVGTransformList.removeElementFromListMap} cb Passed a single argument `elem`
* @returns {void}
*/

export const changeRemoveElementFromListMap = function (cb) {
    removeElementFromListMap = cb;
};
